import React, { useEffect, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import LoadingPage from '../pages/error-pages/LoadingPage.jsx';

import { UserAuth } from './AuthContext.jsx';
import { setGameAccessibility } from '../redux-store/ReduxStore.js';
import { getGameRegistriesByOrgIdWithAccessibility } from '../services/admin-settings/GameService.jsx';
import { GAMES_CHANGED_EVENT } from '../utils/gamesChanged.js';
import { useHeldRefusal } from './accessHold.js';
import { PageHeader } from '../components/page-header/PageHeader.jsx';
import { resolveMenuContext } from '../components/page-header/navLookup.js';
import { useMenuNavTree } from '../components/page-header/useMenuNavTree.js';
import NoGameAccessPlaceholder from '../components/empty-state/NoGameAccessPlaceholder.jsx';
import RouteGameAccessCard from '../components/empty-state/RouteGameAccessCard.jsx';
import { routeGameAccess, routeGameSlug, snapshotIsCurrent } from './routeGameAccess.js';
import { useAccessVersion } from './useAccessRefresh.js';

/**
 * Pick a sensible title for the synthesized PageHeader. Tries the label the
 * current view's nav shows first (so e.g. "/game/<slug>/configs" becomes
 * "Configs"); for routes the nav doesn't list (form sub-routes) we fall back
 * to a title-cased last path segment so the chrome still reads sensibly
 * instead of showing an empty title.
 */
const deriveHeaderTitle = (pathname, navTree) => {
    const ctx = resolveMenuContext(pathname, navTree);
    if (ctx?.currentNode?.label) return ctx.currentNode.label;
    const segs = (pathname || '').split('/').filter(Boolean);
    const last = segs[segs.length - 1] || '';
    if (!last) return '';
    return last
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
};

/**
 * Page-content gate for game-context routes (live-ops, dashboards, analytics,
 * players, data-intelligence, …).
 *
 * Behaviour:
 *  - No selectedOrgId       → render children. The user has no org yet; that's
 *                             ProtectedRoute's domain (it redirects to /onboarding
 *                             or /demo), not ours.
 *  - Snapshot stale/missing → fire getGameRegistriesByOrgIdWithAccessibility,
 *                             show a centered spinner while it lands. The page
 *                             component never mounts during the fetch, so we
 *                             don't waste a render that's about to get replaced
 *                             by the placeholder.
 *  - accessibleCount === 0  → render a synthesized <PageHeader/> (so the
 *                             universal .content-header chrome stays visible
 *                             with a working breadcrumb/section switcher)
 *                             followed by <NoGameAccessPlaceholder/>.
 *  - Otherwise              → render children (or <Outlet/> when used as a
 *                             layout route via <RequireGameAccessOutlet/>).
 *
 * The snapshot lives in Redux (gameAccessibility) keyed by orgId, so multiple
 * pages mounted under the same org reuse the cached value. We re-fetch only
 * when the orgId changes.
 *
 * Note: this is UX gating, not a security boundary. The backend enforces
 * per-game access via RBAC; hiding the page just keeps the user from seeing
 * empty tables / broken charts.
 */
export const RequireGameAccess = ({ children }) => {
    const { user } = UserAuth();
    const dispatch = useDispatch();
    const location = useLocation();
    const selectedOrgId = useSelector(state => state.dashboardContext?.selectedOrgId);
    const selectedGameId = useSelector(state => state.dashboardContext?.selectedGameId);
    const snapshot = useSelector(state => state.gameAccessibility);
    const accessVersion = useAccessVersion();
    const navTree = useMenuNavTree();

    // Re-fetch when the org or the selected game changes, or the user's access changed since the snapshot was taken
    // (accessVersion) - even while no game page was mounted to hear it (snapshotIsCurrent).
    const snapshotMatchesCurrent = snapshotIsCurrent(snapshot,
        { orgId: selectedOrgId, gameId: selectedGameId, accessVersion });
    // Bumped when this tab's view of the caller's games goes stale - a game registered, or the server refusing to open
    // a game (e.g. an owner suspended them mid-session) - so the snapshot is re-read and the page gives way to the card.
    const [refreshTick, setRefreshTick] = useState(0);
    // The tick last fetched for - a ref, so recording it does not re-run (and cancel) the fetch it starts.
    const fetchedTick = useRef(0);
    useEffect(() => {
        const onGamesChanged = () => setRefreshTick(t => t + 1);
        window.addEventListener(GAMES_CHANGED_EVENT, onGamesChanged);
        return () => window.removeEventListener(GAMES_CHANGED_EVENT, onGamesChanged);
    }, []);
    const needsFetch = !!selectedOrgId && !!user?.accessToken && !snapshotMatchesCurrent;

    // If we already have access for this org, render now and re-fetch in the
    // background (no spinner on game switch). Only block when we lack an org
    // snapshot or it says no access (count === 0).
    const orgSnapshotLoaded = snapshot?.loaded && snapshot.orgId === selectedOrgId;
    const haveUsableAccess = orgSnapshotLoaded && snapshot.accessibleCount > 0;

    useEffect(() => {
        const refreshAsked = refreshTick !== fetchedTick.current && !!selectedOrgId && !!user?.accessToken;
        if (!needsFetch && !refreshAsked) return;
        fetchedTick.current = refreshTick;

        let cancelled = false;
        (async () => {
            try {
                const fetchAccessibleCount = async () => {
                    const resp = await getGameRegistriesByOrgIdWithAccessibility({
                        orgId: selectedOrgId,
                        accessToken: user.accessToken
                    });
                    const games = resp?.games || [];
                    return { games, accessibleCount: games.filter(g => !!g?.accessible).length };
                };

                let { games, accessibleCount } = await fetchAccessibleCount();
                if (cancelled) return;

                // Arriving from a just-approved "Refresh access & continue": the new grant can lag the
                // accessibility read by a beat, so a transient 0 would flash the "No game access"
                // placeholder. Retry a few times on 0 (the user keeps seeing the spinner, not the
                // placeholder) before committing — scoped to this one-shot flag so genuine no-access
                // loads aren't delayed.
                let justRefreshed = false;
                try { justRefreshed = sessionStorage.getItem('gp_access_just_refreshed') === '1'; } catch (_) { /* ignore */ }
                if (justRefreshed) {
                    let attempts = 0;
                    while (accessibleCount === 0 && attempts < 3 && !cancelled) {
                        await new Promise(r => setTimeout(r, 700));
                        if (cancelled) return;
                        ({ games, accessibleCount } = await fetchAccessibleCount());
                        attempts += 1;
                    }
                    try { sessionStorage.removeItem('gp_access_just_refreshed'); } catch (_) { /* ignore */ }
                }
                if (cancelled) return;

                dispatch(setGameAccessibility({
                    orgId: selectedOrgId,
                    gameId: selectedGameId,
                    accessVersion,
                    accessibleCount,
                    total: games.length,
                    // Kept so a /game/<slug> URL can be checked against it (routeGameAccess.js).
                    games: games.map(g => ({
                        id: g?.game?.id || g?.id,
                        orgId: g?.game?.orgId || selectedOrgId,
                        gameName: g?.game?.gameName || g?.gameName,
                        accessible: !!g?.accessible,
                        // Suspended on it by an owner: the card says so instead of offering a request.
                        suspended: !!g?.suspended,
                    })).filter(g => g.id),
                }));
            } catch (e) {
                if (cancelled) return;
                // Treat fetch failure as "we don't know yet" — leave the snapshot
                // empty but mark it loaded so we fall through to the page. The
                // page will surface its own error if the user actually has no
                // access; better that than locking everyone out on a transient
                // network blip.
                console.error('RequireGameAccess: failed to fetch game accessibility', e);
                dispatch(setGameAccessibility({
                    orgId: selectedOrgId,
                    gameId: selectedGameId,
                    accessVersion,
                    accessibleCount: 1, // optimistic — let the page render
                    total: 0
                }));
            }
        })();

        return () => { cancelled = true; };
    }, [needsFetch, selectedOrgId, selectedGameId, user?.accessToken, dispatch, refreshTick, accessVersion]);

    // What this guard decides for the URL, worked out before any return: a page that was open when access was
    // re-read is held (accessHold.js) - the refusal is reported for the banner and the page stays.
    const routeAccess = orgSnapshotLoaded ? routeGameAccess(location.pathname, snapshot.games) : { state: 'none' };
    const noGameAccess = orgSnapshotLoaded && snapshot.accessibleCount === 0;
    const routeRefused = routeAccess.state === 'locked' || routeAccess.state === 'unknown'
        || routeAccess.state === 'suspended';
    const refusedName = routeAccess.game?.gameName || 'this game';
    const keepPage = useHeldRefusal(location.pathname, !!selectedOrgId && (noGameAccess || routeRefused),
        routeAccess.state === 'suspended'
            ? `Your access to ${refusedName} was suspended.`
            : `You no longer have access to ${refusedName}.`);

    // No org selected — let upstream guards handle it (ProtectedRoute redirects
    // org-less users to /onboarding before they ever reach a gated route).
    if (!selectedOrgId) return children;
    if (keepPage) return children;

    // Block with the standard loader (not a bare "dotted" spinner) only when we have NO decision for
    // this org yet — so the org-switch wait matches every page's LoadingPage instead of flashing a
    // different spinner before the page's own loader.
    //
    // "No decision" is org-level on purpose. Keying it on haveUsableAccess (count > 0) meant a user
    // with zero access never satisfied it: every re-fetch — and the snapshot is re-keyed on the
    // selected game, so navigating re-triggers one — put them back on the spinner instead of the
    // placeholder that already had their answer. The one person this gate exists for was the one
    // person it never showed the card to.
    if (needsFetch && !haveUsableAccess && !orgSnapshotLoaded) {
        return <LoadingPage />;
    }

    if (noGameAccess) {
        // Synthesize the universal .content-header chrome on top of the
        // placeholder. Pages normally render their own <PageHeader/>, but
        // we never get to that render path when access is blocked — so we
        // produce an equivalent header here using the current route's
        // navConfig label. The breadcrumb dropdown still works because
        // PageHeader self-resolves its menu context from `pagePath`, which
        // means the user can still hop to other sections from a blocked page.
        return (
            <>
                <PageHeader
                    title={deriveHeaderTitle(location.pathname, navTree)}
                    pagePath={location.pathname}
                    showScope={false}
                />
                <NoGameAccessPlaceholder />
            </>
        );
    }

    // The URL names a game: one the user cannot open, or none of this org's, gets a card that says so instead of
    // its pages - which otherwise render and fail panel by panel ("Failed to load segments - Insufficient
    // permissions"). Decided only from this org's loaded catalogue; without it, the pages render as before.
    if (routeRefused) {
        return (
            <>
                <PageHeader
                    title={routeAccess.game?.gameName || deriveHeaderTitle(location.pathname, navTree)}
                    pagePath={location.pathname}
                    showScope={false}
                />
                <RouteGameAccessCard
                    state={routeAccess.state}
                    game={routeAccess.game}
                    slug={routeGameSlug(location.pathname)}
                />
            </>
        );
    }

    return children;
};

/**
 * Layout-route shorthand for wrapping a whole children block in one shot:
 *
 *     children: [
 *         { element: <RequireGameAccessOutlet />, children: [ ...gatedRoutes ] }
 *     ]
 *
 * Keeps route definitions readable by tagging the boundary once rather than
 * wrapping every leaf route's element individually.
 */
export const RequireGameAccessOutlet = () => (
    <RequireGameAccess><Outlet /></RequireGameAccess>
);

export default RequireGameAccess;
